"use client";
import { useEffect, useState } from "react";
import { useBlock } from "./context";
import { TextAttr } from "./attributes";
import { CONSENT_SET_EVENT, onConsentChange, openConsentPreferences, readStoredConsent, validConsent } from "../consent/client/store";
import { defaultConsentTexts } from "../consent/texts";
import type { PrivacyPolicyData } from "../consent/policy";
import type { StoredConsent } from "../consent/types";

/**
 * Live privacy statement blocks (docs/cookie-consent-plan.md §7.1). The
 * content is generated on the server from the service register
 * (src/core/consent/policy.ts → blockData), so it can never drift from what
 * the site actually loads.
 */

const box = { maxWidth: "56rem", marginInline: "auto", padding: "2rem 1.5rem", lineHeight: 1.65 } as const;

function CookieTable({ data }: { data: PrivacyPolicyData }) {
  const t = defaultConsentTexts(data.locale);
  return <div className="cms-cookie-table">
    {data.cookies.map((group) => <section key={group.category}>
      <h3>{group.label}</h3>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.92em" }}>
          <thead><tr>{["Cookie", t.provider, t.retention].map((head) => <th key={head} scope="col" style={{ textAlign: "left", padding: "6px 8px", borderBottom: "2px solid currentColor" }}>{head}</th>)}</tr></thead>
          <tbody>
            {group.services.flatMap((service) => (service.cookies.length ? service.cookies : [{ name: "—", retention: "—", thirdParty: undefined }]).map((cookie, index) => (
              <tr key={`${service.name}-${cookie.name}`}>
                <td style={{ padding: "6px 8px", borderBottom: "1px solid rgba(127,127,127,.35)", verticalAlign: "top" }}>
                  <code>{cookie.name}</code>{cookie.thirdParty && <><br /><small>{t.thirdParty.replace("{domain}", cookie.thirdParty)}</small></>}
                  {index === 0 && <><br /><small><strong>{service.name}</strong> — {service.purpose} ({service.basis})</small></>}
                </td>
                <td style={{ padding: "6px 8px", borderBottom: "1px solid rgba(127,127,127,.35)", verticalAlign: "top" }}>{service.provider}</td>
                <td style={{ padding: "6px 8px", borderBottom: "1px solid rgba(127,127,127,.35)", verticalAlign: "top" }}>{cookie.retention}</td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </section>)}
  </div>;
}

/** Current choice with consent ID, plus change and withdraw buttons (AP: withdrawal at the top of the cookie policy). */
export function CookiePreferencesPanel({ locale }: { locale: string }) {
  const t = defaultConsentTexts(locale);
  const [state, setState] = useState<StoredConsent | null>(null);
  const [texts, setTexts] = useState(t);
  useEffect(() => {
    const config = window.__cmsConsentConfig;
    if (config) setTexts({ ...t, ...config.texts });
    const read = () => setState(config ? validConsent(readStoredConsent(), config) : readStoredConsent());
    read();
    return onConsentChange(read);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const button = { minHeight: 44, padding: "10px 16px", borderRadius: 8, border: "2px solid currentColor", background: "transparent", color: "inherit", font: "inherit", fontWeight: 600, cursor: "pointer" } as const;
  return <div className="cms-cookie-preferences" style={{ border: "1px solid rgba(127,127,127,.4)", borderRadius: 10, padding: 16, display: "grid", gap: 10 }}>
    <strong>{texts.dialogTitle}</strong>
    {state
      ? <span>{texts.consentId}: <code>{state.id}</code> · {texts.consentDate} {new Date(state.ts).toLocaleString(locale)}</span>
      : <span>{texts.noChoice}</span>}
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      <button type="button" style={button} onClick={openConsentPreferences}>{texts.preferences}</button>
      {state && <button type="button" style={button} onClick={() => window.dispatchEvent(new CustomEvent(CONSENT_SET_EVENT, { detail: { action: "reject_all", source: "policy_page" } }))}>{texts.rejectAll}</button>}
    </div>
  </div>;
}

export function PrivacyPolicyBlock() {
  const { data, locale, editing } = useBlock<PrivacyPolicyData | null>();
  if (!data) return <section style={box}>{editing ? <p>De privacyverklaring wordt automatisch samengesteld uit de diensten en cookies van deze website.</p> : null}</section>;
  return <article className="cms-privacy-policy" style={box}>
    <TextAttr name="heading" as="h1" />
    {data.intro && <div dangerouslySetInnerHTML={{ __html: data.intro }} />}
    {data.choices && <CookiePreferencesPanel locale={locale} />}
    {data.sections.map((section) => <section key={section.id} id={`privacy-${section.id}`}>
      <h2>{section.heading}</h2>
      <div dangerouslySetInnerHTML={{ __html: section.html }} />
      {section.id === "cookies" && <CookieTable data={data} />}
    </section>)}
    {data.extra && <div dangerouslySetInnerHTML={{ __html: data.extra }} />}
  </article>;
}

export function CookieTableBlock() {
  const { data } = useBlock<PrivacyPolicyData | null>();
  if (!data) return null;
  return <section style={box}><CookieTable data={data} /></section>;
}

export function CookiePreferencesBlock() {
  const { locale } = useBlock();
  return <section style={box}><CookiePreferencesPanel locale={locale} /></section>;
}
